import moment from 'moment';
import { Inject } from '@odinjs/odin';
import { CheckboxAPI, FetchAPI, FieldAPI, FormAPI, InputAPI, ToastService } from '@philips-internal/oswald-ui-components';
import Controller, { OnBeforeSubmit, OnCancel, OnChange, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { HttpService, MessageService, Validator } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { ChangeEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import { CanScheduleTime, InfoSchedules, ReasonField, ResponseConsist, ResponseSaveStandardSchedules, ScheduleMedicationParams} from './schedule-medication-infusion-params';
import BaseController from '../BaseController';
import { SimpleDialog } from '../dialog-helper';
import TimelineContext, { PrescriptionType, TimeLineEventMedication } from '../adep/services/timeline-service';
import { MedicationViewerParams } from '../adep/eletronic-prescription/medication-viewer/medication-viewer-params';
import { MedicineAndSolutionsParams } from '../adep/eletronic-prescription/medicine-and-solutions/medicine-and-solutions-params';
import { MedicationAndDateHeaderParams } from '../commons/header/medication-and-date-header-params';
import { DialogType, ModalType, SimpleDialogParams } from '../../interfaces/simple-dialog';
import { Endpoint } from '../../constants/endpoints';
import { ApplicationRoute } from '../../constants/routes';
import { FoundationUtils } from '../../utils/foundation-utils';
import { YesNoType } from '../../utils/enum-utils';
import { BooleanUtils } from '../../utils/boolean-utils';
import { ADEP_E1113Parameters, FunctionName } from '../../utils/parameters';
import { FragmentNames } from '../../codegen';
import { ScheduleMedicationInfusionsAliases } from '../../codegen/constants/aliases/schedule-medication-infusions-aliases';
import { ISO_FULL_DATE_FORMAT, TIME_ONLY} from '../../constants/date-utils';
import { Value } from '@philips-internal/oswald-ui-core';

const eliminetedTime = 510990;
const exceededScheduleTime = 1068496;
let dateReplace: string;

@Controller({ fragment: FragmentNames.SCHEDULE_MEDICATION_INFUSIONS })
class ScheduleMedicationInfusionsController extends BaseController {

  @Inject(ScheduleMedicationInfusionsAliases.CHECKBOX_SCHEDULE)
  private fieldCheckboxApi!: CheckboxAPI;

  @Inject(ScheduleMedicationInfusionsAliases.FIELD_SCHEDULE_PREVIOUS)
  private fieldSchedulePrevious!: FieldAPI;

  @Inject(ScheduleMedicationInfusionsAliases.INPUT_DESIRED_TIME)
  private inputDesiredTimeApi!: InputAPI;

  @Inject(ScheduleMedicationInfusionsAliases.FIELD_JUSTIFICATION)
  private fieldJustificationApi!: FieldAPI;

  @Inject(ScheduleMedicationInfusionsAliases.FIELD_REASON)
  private fieldReasonApi!: FieldAPI;

  @Inject(ScheduleMedicationInfusionsAliases.FORM_SCHEDULE)
  private formApi!: FormAPI;

  @Inject(ScheduleMedicationInfusionsAliases.FIELD_DESIRED_TIME)
  private fieldDesiredApi!: FieldAPI;

  @Inject(ScheduleMedicationInfusionsAliases.FIELD_SAVE_DEFAULT_TIMES)
  private inputSaveDefaultTimes!: FieldAPI;

  @Inject(ScheduleMedicationInfusionsAliases.FIELD_STANDARD_TIME_SLOTS)
  private fieldStandardTimeSlots!: FieldAPI;

  @Inject(ScheduleMedicationInfusionsAliases.CHECKBOX_SAVE_DEFAULT_TIMES)
  private fieldCheckBoxSaveDefaultTimes!: CheckboxAPI;

  @Inject(ScheduleMedicationInfusionsAliases.INPUT_STANDARD_TIME_SLOTS)
  private inputStandardTimeSlots!: InputAPI;

  @OnMount(ScheduleMedicationInfusionsAliases.FETCH_REASON)
  handleFetchReasonMount(event: MountEvent): void {
    const fetchReason = event.target as FetchAPI;
    const establishmentCode = foundationContext.getEstablishment()?.id;

    fetchReason.setParameters({ establishmentCode }, { skip: true });
  }

  @OnMount(ScheduleMedicationInfusionsAliases.FORM_SCHEDULE)
  handleMountForm(): void {
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    const establishment = foundationContext.getEstablishment()?.id;
    const canScheduleTime = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.FIELD_TO_BE_REQUIRED_FOR_RESCHEDULE)
    this.context.set({
      canScheduleTime,
      encounterId,
      establishment,
      patientId,
      status: MedicineAndSolutionsParams.params.ieTypeItem
    });
  }

  private getIntevralCode(): Promise<string | void> {
    const cdIntervalId = MedicineAndSolutionsParams.params.cdIntervalId;
    return HttpService.get(Endpoint.GET_OPERATION_INTERVAL, { cdIntervalId })
      .then((response) => {
        return response.body as string;
      }).catch((err) => {
        this.infoCatch(err);
      });
  }

  infoCatch (err: any): void {
    const errorMessage = err.body?.errors?.join(' \n');
    this.showToast(errorMessage, ToastLevel.WARNING);
  }

  async onAfterLoad(): Promise<void> {
    if (this.fieldSchedulePrevious.isVisible()) {
      const param100 = this.showRescheduleFollowingHoursByDefault();
      this.fieldSchedulePrevious.setValue(BooleanUtils.isPositiveValue(param100));
    }
    const operationInterval = await this.getIntevralCode() as string;
    const getIeACMSN = MedicineAndSolutionsParams.params.ieAcmId;
    const itemType = MedicineAndSolutionsParams.params.ieTypeItem as PrescriptionType;
    const isAllowedType = [PrescriptionType.SOLUTION, PrescriptionType.MEDICINE].includes(itemType);
    const standardSchedules = this.showCheckBoxSaveDefaultTimes() &&
      isAllowedType && !['F', 'V'].includes(operationInterval) && getIeACMSN != 'S';

    if (isAllowedType) {
      this.fieldsBehavior(standardSchedules);
      this.defineSchedules(standardSchedules, moment(this.fieldDesiredApi.getValue() as string).format(ISO_FULL_DATE_FORMAT));
    }
  }

  saveStandardSchedules(): void {
    if (this.fieldStandardTimeSlots.isVisible()) {
      const schedules = dateReplace;
      const itemCode = TimelineContext.get().itemCode;
      const { patientId, encounterId } = this.historyService.getCurrentRouteParams();

      const body = {
        cpoeSequence: TimelineContext.get().cpoeSequence,
        encounterId: Number(encounterId),
        intervalId: MedicineAndSolutionsParams.params.cdIntervalId,
        itemCode: itemCode,
        itemType: MedicineAndSolutionsParams.params.ieTypeItem,
        patientId: String(patientId),
        prescriptionNumber: MedicineAndSolutionsParams.params.nrPrescriptionId,
        prescriptionsNumber: String(MedicineAndSolutionsParams.params.nrPrescriptionId),
        schedules: schedules,
        scheduleSequence: Number(MedicineAndSolutionsParams.params.id),
      };

      HttpService.post(Endpoint.SAVE_STANDARD_SCHEDULES, {}, { body })
        .then((response) => {
          const res = response.body as ResponseSaveStandardSchedules;
          if (res?.errorDescription) {
            const warMsg = res.errorDescription;
            this.showToast(warMsg, ToastLevel.WARNING);
            return
          }
          if(res?.result == null) {
            return
          }
        }).catch((err) => {
          const errorMsg = err.body?.errorDescription?.join(' \n');
          if (errorMsg) {
            this.showToast(errorMsg, ToastLevel.WARNING);
          } else {
            this.showToast(MessageService.translate('text.not-possible-default-schedules'), ToastLevel.WARNING);
          }
        });
    }
  }

  defineSchedules(standardSchedules: boolean, desiredDate: string): void {
    const currenteDate = MedicineAndSolutionsParams.params.startId as string;
    const rescheduleFutureSchedules = BooleanUtils.isPositiveValue(this.fieldCheckboxApi.getValue());
    const intervalId = MedicineAndSolutionsParams.params.cdIntervalId;
    const body = {
      cpoeSequence: TimelineContext.get().cpoeSequence,
      currenteDate,
      desiredDate,
      intervalId: intervalId ? intervalId : null,
      itemCode: TimelineContext.get().itemCode,
      itemType: MedicineAndSolutionsParams.params.ieTypeItem,
      prescriptionNumber: MedicineAndSolutionsParams.params.nrPrescriptionId,
      rescheduleFutureSchedules: rescheduleFutureSchedules || standardSchedules ? YesNoType.YES : YesNoType.NO,
      schedulePrevious: BooleanUtils.isPositiveValue(this.fieldCheckboxApi.getValue()),
      scheduleSequence: Number(MedicineAndSolutionsParams.params.id),
    };

    this.fieldStandardTimeSlots.setVisible(Boolean(this.inputSaveDefaultTimes.getValue()));
    if (!this.inputSaveDefaultTimes.isVisible()) {
      return
    }

    this.calculatePrescribingSchedules(body);
  }

  private calculatePrescribingSchedules(body: Record<string, Value>): void {
    HttpService.post(Endpoint.CALCULATE_PRESCRIBING_SCHEDULES, {}, { body })
      .then((response) => {
        const res = response.body as InfoSchedules;
        if (res && this.fieldStandardTimeSlots.isVisible()) {
          this.dateReplaced(res);
          const arrayDate = res?.schedules.trim().split(' ').map(schedule => moment(schedule, TIME_ONLY).format(TIME_ONLY));
          this.inputStandardTimeSlots.setValue(String(arrayDate.join(', ')));
        }
        return
      }).catch((err) => {
        this.infoCatch(err);
      });
  }

  private dateReplaced(res: InfoSchedules) : void {
    const newDate = res?.schedules.trim().split(' ').map(schedule => moment(schedule, 'HH:mm').format('HH:mm'));
    dateReplace = newDate.join(', ').replaceAll(', ', ' ');
  }

  private fieldsBehavior(standardSchedules: boolean): void {
    this.setTimeSchedule(standardSchedules);
    this.consistirParametro637();
  }

  private setTimeSchedule(standardSchedules: boolean): void {
    const desiredDate = moment(this.fieldDesiredApi.getValue() as string).format(ISO_FULL_DATE_FORMAT);
    const currenteDate = MedicineAndSolutionsParams.params.startId as string;

    if (currenteDate && desiredDate) {
      this.defineSchedules((this.fieldCheckBoxSaveDefaultTimes.getValue()
        && this.fieldCheckboxApi.getValue())
        && standardSchedules, desiredDate);
    }
  }
  private consistirParametro637(): void {
    const desiredDate = moment(this.fieldDesiredApi.getValue() as string).format(ISO_FULL_DATE_FORMAT);
    const currenteDate = MedicineAndSolutionsParams.params.startId as string;
    const param637 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.KEEP_THE_OPTION_RESCHEDULE_FOLLOWING_TIMES_ALWAYS_CHECKED_EVEN_WHEN_RESCHEDULING_TO_AN_EARLIER_TIME);

    if (currenteDate > desiredDate && param637 !== YesNoType.YES) {
      this.fieldCheckboxApi.setValue(false);
    }
  }

  @OnMount(ScheduleMedicationInfusionsAliases.FIELD_SAVE_DEFAULT_TIMES)
  handleMountFieldSaveDefaultTimes(): void {
    const itemType = MedicineAndSolutionsParams.params.ieTypeItem as string;
    const validItems = [PrescriptionType.MEDICINE.valueOf(), PrescriptionType.SOLUTION.valueOf()];

    if (this.showCheckBoxSaveDefaultTimes() && validItems.includes(itemType)) {
      this.fieldStandardTimeSlots.setVisible(false);
      this.onAfterLoad();
    } else {
      this.fieldStandardTimeSlots.setVisible(false);
      this.inputSaveDefaultTimes.setVisible(false);
    }
  }

  @OnChange(ScheduleMedicationInfusionsAliases.FIELD_DESIRED_TIME)
  handleChangeFieldDesiredTime(event: ChangeEvent<FieldAPI>): void {
    if (this.inputSaveDefaultTimes.isVisible() && this.fieldSchedulePrevious.getValue()) {
      const currentDate = moment(event.value as unknown as string);
      if (!currentDate.isValid()) {
        return;
      }
      const desiredDate = currentDate.format(ISO_FULL_DATE_FORMAT);
      if (this.fieldCheckBoxSaveDefaultTimes.getValue() && currentDate.isValid() || this.inputSaveDefaultTimes.isVisible()) {
        this.defineSchedules(this.fieldCheckboxApi.getValue(), desiredDate);
      }
    }
  }

  @OnChange(ScheduleMedicationInfusionsAliases.FIELD_SAVE_DEFAULT_TIMES)
  handleClickSaveDefaultTimesAction(event: ChangeEvent<FieldAPI>): void {
    const valueCheckBox = event.value as unknown as boolean;
    if (valueCheckBox) {
      this.onAfterLoad();
      return
    }
    this.fieldStandardTimeSlots.setVisible(valueCheckBox);
  }

  @OnChange(ScheduleMedicationInfusionsAliases.CHECKBOX_SCHEDULE)
  handleCheckSChedule(event: ChangeEvent<FieldAPI>): void {
    const valueCheckBox = event.value as unknown as boolean;
    const desiredDate = moment(this.fieldDesiredApi.getValue() as string).format(ISO_FULL_DATE_FORMAT);
    this.fieldCheckboxApi.setValue(valueCheckBox);
    if(valueCheckBox){
      this.defineSchedules(valueCheckBox, desiredDate);
      return
    }

    this.defineSchedules(valueCheckBox, MedicineAndSolutionsParams.params.startId as string);
  }

  private showCheckBoxSaveDefaultTimes(): boolean {
    return FoundationUtils.findModuleParam(
      FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_SAVE_DEFAULT_TIME_SLOTS_OF_THE_ITEM_WHEN_RESCHEDULING_THE_TIME) === YesNoType.YES;
  }

  private showRescheduleFollowingHoursByDefault(): boolean {
    return FoundationUtils.findModuleParam(
      FunctionName.ADEP_1113, ADEP_E1113Parameters.WHILE_RESCHEDULE_SHOW_RESCHEDULE_FOLLOWING_HOURS_CHECKBOX_BY_DEFAULT) === YesNoType.YES;
  }

  @OnMount(ScheduleMedicationInfusionsAliases.FIELD_SCHEDULE_PREVIOUS)
  handleMountFieldSchedulePrevious(): void {
    const itemType = MedicineAndSolutionsParams.params.ieTypeItem as string;
    const validItems = [PrescriptionType.MEDICINE.valueOf(), PrescriptionType.ORAL_SUPPLEMENTS.valueOf(), PrescriptionType.RECOMMENDATIONS.valueOf()]

    if (!validItems.includes(itemType)) {
      this.fieldSchedulePrevious.setVisible(false)
    }

    if (this.showRescheduleFollowingHoursByDefault()) {
      this.fieldSchedulePrevious.setVisible(true);
      this.fieldCheckboxApi.setValue(false);
    }
  }

  @OnMount(ScheduleMedicationInfusionsAliases.INPUT_DESIRED_TIME)
  onMountInputDesireTime(): void {
    const startId = MedicineAndSolutionsParams.params.startId;
    this.inputDesiredTimeApi.setValue(startId ?? '');
  }

  @OnMount(ScheduleMedicationInfusionsAliases.FIELD_JUSTIFICATION)
  handleMountFieldJustification({ target }: EventData<FieldAPI>): void {
    const validParameters = [CanScheduleTime.BOTH.valueOf(), CanScheduleTime.JUSTIFICATION.valueOf()]
    this.validateFieldToBeRequired(target, validParameters);
  }

  @OnMount(ScheduleMedicationInfusionsAliases.FIELD_REASON)
  handleMountFieldReason({ target }: EventData<FieldAPI>): void {
    const validParameters = [CanScheduleTime.BOTH.valueOf(), CanScheduleTime.REASON.valueOf()]
    this.validateFieldToBeRequired(target, validParameters);
  }

  private validateFieldToBeRequired(field: FieldAPI, validParameters: string[]): void {
    const canScheduleTime = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.FIELD_TO_BE_REQUIRED_FOR_RESCHEDULE) ?? ''
    const validator: Validator = { name: 'required', options: {} };
    if (validParameters.includes(canScheduleTime as string)) {
      field.setValidators([validator]);
      field.validate();
    }
  }

  @OnBeforeSubmit(ScheduleMedicationInfusionsAliases.FORM_SCHEDULE)
  handleClickButtonSchedule(): void {
    const desiredDate = moment(this.inputDesiredTimeApi.getValue());
    const formatedScheduleDate = moment(MedicationAndDateHeaderParams.medicationAndDateHeaderProps?.scheduleDate);
    if (desiredDate.diff(formatedScheduleDate, 'seconds') === 0) {
      this.showToast('tws.schedule-medication-time-error', ToastLevel.WARNING);
      return;
    }

    if (this.context.get('status') === PrescriptionType.ENTERAL_NUTRITION) {
      this.consistEnteralNutrition();
      return;
    }

    if (this.canSubmit()) {
      this.formApi.validate();
      this.showToast('tws.schedule-medication-infusion-required', ToastLevel.WARNING);
      return;
    }

    if (this.context.get('status') === PrescriptionType.SOLUTION) {
      this.consistAndScheduleSolution();
      return;
    }

    this.consistScheduleMedication();
  }

  @OnCancel(ScheduleMedicationInfusionsAliases.FORM_SCHEDULE)
  handleCancelForm(): void {
    applicationContext.popNavigationItem();
  }

  private createConsistAndScheduleSolutionRequestBody(solutionType: number, currentStepNumber: number, solutionNumber: string | undefined): Record<string, Value> {
    const reasonNumber = this.fieldReasonApi.getValue() as unknown as ReasonField;
    const reason = reasonNumber != null ? reasonNumber.sequenceNumber : null;
    const desired = moment(this.fieldDesiredApi.getValue() as string).format(ISO_FULL_DATE_FORMAT);
    const schedulePrevious = BooleanUtils.isPositiveValue(this.fieldCheckboxApi.getValue());
    return {
      currentStepNumber,
      initialDate: desired,
      observationDescription: this.fieldJustificationApi.getValue(),
      prescriptionNumber: MedicineAndSolutionsParams.params.nrPrescriptionId,
      reasonNumber: reason,
      scheduleItemCycle: YesNoType.NO,
      scheduleStatus: schedulePrevious,
      solutionNumber,
      solutionStatus: solutionType,
      timeNumber: ScheduleMedicationParams.prototype.scheduleSequenceNumber || 0,
      userName: foundationContext.getUser()?.username,
    }
  }

  private consistEnteralNutrition(): void {
    const param175 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.CAN_CHANGE_INITIAL_DATA_SOLUTION)

    if (param175 !== YesNoType.YES) {
      const currentDate = MedicineAndSolutionsParams.params.startId ?? '';
      const date = moment(currentDate).utc().format(ISO_FULL_DATE_FORMAT);
      const dateDesisred = moment(this.inputDesiredTimeApi.getValue())

      if (dateDesisred.isBefore(date)) {
        this.showToast('tws.cant-schedule-infusons', ToastLevel.WARNING);
        return
      }
    }

    const enteralNutritionType = 2
    const scheduleSequence = MedicineAndSolutionsParams.params.hourSequence
    const solutionNumber = MedicineAndSolutionsParams.params.nrSequenceId
    const body = this.createConsistAndScheduleSolutionRequestBody(enteralNutritionType, Number(scheduleSequence), solutionNumber);
    HttpService.post(
      Endpoint.CONSIST_SOLUTION, {}, { body }
    ).then(response => {
      const res = response.body as string;
      const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
      applicationContext.redirect(ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION, {
        encounterId,
        patientId,
      });
      applicationContext.clearNavigationStack();

      if (res) {
        return this.showToast(res, ToastLevel.WARNING);
      }

      return this.showToast('tws.schedule-medication-infusions-success', ToastLevel.SUCCESS);
    }).catch((err) => {
      this.infoCatch(err);
    });
  }

  private consistAndScheduleSolution(): void {
    const module = foundationContext.getAvailableModules();
    const moduleExamCollection = module?.find(item => item.id === FunctionName.ADEP_1113);

    let canScheduleSolution = '';
    if (moduleExamCollection?.parameters.get(ADEP_E1113Parameters.CAN_CHANGE_INITIAL_DATA_SOLUTION)) {
      canScheduleSolution = moduleExamCollection?.parameters.get(ADEP_E1113Parameters.CAN_CHANGE_INITIAL_DATA_SOLUTION) as string;
    }

    if (foundationContext.getModuleParameter(FunctionName.ADEP_1113, ADEP_E1113Parameters.CAN_CHANGE_INITIAL_DATA_SOLUTION) as string) {
      canScheduleSolution = foundationContext.getModuleParameter(FunctionName.ADEP_1113, ADEP_E1113Parameters.CAN_CHANGE_INITIAL_DATA_SOLUTION) as string;
    }

    const currentDate = MedicineAndSolutionsParams.params.startId ?? '';
    const date = moment(currentDate);
    const dateDesisred = moment(this.inputDesiredTimeApi.getValue())

    if (canScheduleSolution === YesNoType.YES || dateDesisred.isAfter(date)) {
      const solutionType = 1
      const currentStepNumber = ScheduleMedicationParams.prototype.solutionNumber
      const solutionNumber = MedicineAndSolutionsParams.params.nrSequenceId
      const body = this.createConsistAndScheduleSolutionRequestBody(solutionType, currentStepNumber, solutionNumber);
      this.saveStandardSchedules();
      HttpService.post(
        Endpoint.CONSIST_SOLUTION, {}, { body }
      ).then(response => {
        const res = response.body as string;
        const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
        applicationContext.redirect(ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION, {
          encounterId,
          patientId,
        });
        applicationContext.clearNavigationStack();

        if (res) {
          this.showToast(res, ToastLevel.WARNING);
        } else {
          this.showToast('tws.schedule-medication-infusions-success', ToastLevel.SUCCESS);
        }

      }).catch((err) => {
        this.infoCatch(err);
      });
    } else {
      this.showToast('tws.cant-schedule-infusons', ToastLevel.WARNING);
    }
  }

  private createConsistScheduleMedicationRequestBody(): Record<string, Value> {
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const consistReschedulingItemTime = String(FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.SHOW_CONSISTENCY_REGARDING_THE_RESCHEDULIN_OF_AN_ITEM));
    const reasonNumber = this.fieldReasonApi.getValue() as unknown as ReasonField;
    const schedulePrevious = BooleanUtils.isPositiveValue(this.fieldCheckboxApi.getValue());
    const desired = moment(this.fieldDesiredApi.getValue() as string).format(ISO_FULL_DATE_FORMAT);
    const reason = reasonNumber?.sequenceNumber;
    const prescriptionsIds = MedicineAndSolutionsParams.params.nrPrescriptionsId ?? String(MedicineAndSolutionsParams.params.nrPrescriptionId)

    return {
      consistRescheduleItemTime: consistReschedulingItemTime,
      currentTime: MedicineAndSolutionsParams.params.startId,
      desiredTime: desired,
      encounterNumber: Number(encounterId),
      establishmentCode: foundationContext.getEstablishment()?.id,
      internalProcedureNumber: 0,
      intervalCode: MedicineAndSolutionsParams.params.cdIntervalId,
      itemCode: MedicineAndSolutionsParams.params.cdMaterialId,
      itemQuantity: MedicineAndSolutionsParams.params.qtDoseId,
      itemSequence: MedicineAndSolutionsParams.params.nrSequenceId,
      itemType: MedicineAndSolutionsParams.params.ieTypeItem,
      justificationDescription: this.fieldJustificationApi.getValue() ?? '',
      laboratoryStatus: YesNoType.NO,
      prescriptionsIds,
      reasonNumber: reason,
      scheduleTime: schedulePrevious,
      userName: foundationContext.getUser()?.username
    }
  }

  private consistScheduleMedication(): void {
    const body = this.createConsistScheduleMedicationRequestBody();
    const params = {
      prescriptionNumber: MedicineAndSolutionsParams.params.nrPrescriptionId
    }

    this.formApi.setLoading(true)
    HttpService.post(
      Endpoint.CONSIST_SCHEDULE, params, { body }
    ).then(response => {
      const { message, eliminatedList } = response.body as ResponseConsist;
      this.consistScheduleShowMessage(message, eliminatedList)
    });
  }

  private consistScheduleShowMessage(message: number, eliminatedList?: string): void {
    const medicineItens = [PrescriptionType.MEDICINE.valueOf(), PrescriptionType.ORAL_SUPPLEMENTS.valueOf(), PrescriptionType.RECOMMENDATIONS.valueOf()]
    const itemType = MedicineAndSolutionsParams.params.ieTypeItem as string;
    switch (message) {
      case 616381:
        this.confirmCancel(eliminatedList);
        break;
      case 739552:
        this.showToast('tws.schedule-medication-infusion-error', ToastLevel.ERROR);
        break;
      case 510988:
        this.showToast('tws.schedule-medication-infusions-validation-date', ToastLevel.ERROR);
        applicationContext.popNavigationItem();
        break;
      case 0 && medicineItens.includes(itemType):
        this.scheduleTimeItem();
        break;
      default:
        break;
    }
  }

  canSubmit(): boolean {
    const canScheduleTime = this.context.get('canScheduleTime');
    const fieldJustification = this.fieldJustificationApi.getValue();
    const fieldReason = this.fieldReasonApi.getValue();
    const isJustification = canScheduleTime === CanScheduleTime.JUSTIFICATION && fieldJustification
    const isReason = canScheduleTime === CanScheduleTime.REASON && fieldReason
    const isJustificationAndReason = canScheduleTime === CanScheduleTime.BOTH && (fieldJustification || fieldReason)
    const isEmptyParam = canScheduleTime === CanScheduleTime.NONE || !canScheduleTime
    if (isJustification || isReason || isJustificationAndReason || isEmptyParam) {
      return false;
    }

    return true;
  }

  confirmCancel(value?: string): void {
    const message = MessageService.translate('tws.schedule-medicadication-infusion-reschedule').replace('#@horarios#@', `${value}`);

    const dialogParams: SimpleDialogParams = {
      closeable: false,
      descriptionDialog: message,
      dialogType: DialogType.ACTION,
      modalType: ModalType.DIALOG,
      onClickCancel: () => {
        this.formApi.setLoading(false);
        applicationContext.popNavigationItem();
      },
      onClickOk: () => { this.scheduleTimeItem() },
      titleDialog: '',
      titleOnCancel: 'label.status-no',
      titleOnOk: 'label.status-yes',
    }

    SimpleDialog.openDialog(dialogParams);
  }

  private createScheduleTimeItemRequestBody(): Record<string, Value> {
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const schedulePrevious = BooleanUtils.isPositiveValue(this.fieldCheckboxApi.getValue());
    const consistReschedulingItemTime = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.SHOW_CONSISTENCY_REGARDING_THE_RESCHEDULIN_OF_AN_ITEM) as string;
    const statusAcm = BooleanUtils.isPositiveValue(MedicineAndSolutionsParams.params.pdoPrn);
    const desired = moment(this.fieldDesiredApi.getValue() as string).format(ISO_FULL_DATE_FORMAT);
    const reasonNumber = this.fieldReasonApi.getValue() as unknown as ReasonField;
    const reason = reasonNumber?.sequenceNumber;
    const { cpoeSequence } = TimelineContext.get() as TimeLineEventMedication;

    return {
      consistRescheduleItemTime: consistReschedulingItemTime,
      cpoeNumber: cpoeSequence,
      currentTime: MedicineAndSolutionsParams.params.startId,
      desiredTime: desired,
      encounterNumber: Number(encounterId),
      establishmentCode: foundationContext.getEstablishment()?.id,
      functionCode: 9129,
      internalProcedureNumber: MedicineAndSolutionsParams.params.internalProcedureSequence,
      intervalCode: MedicineAndSolutionsParams.params.cdIntervalId,
      itemCode: MedicineAndSolutionsParams.params.cdMaterialId,
      itemDescription: MedicineAndSolutionsParams.params.dsMaterialId,
      itemQuantity: MedicineAndSolutionsParams.params.qtDoseId,
      itemSequence: MedicineAndSolutionsParams.params.nrSequenceId,
      itemType: MedicineAndSolutionsParams.params.ieTypeItem,
      justificationDescription: this.fieldJustificationApi.getValue() ?? '',
      laboratoryStatus: YesNoType.NO,
      observationDescription: MedicineAndSolutionsParams.params.observationDescription,
      prescriptionId: MedicationViewerParams.adminSchedule.prescriptionNumber,
      prescriptionsIds: MedicineAndSolutionsParams.params.nrPrescriptionsId ?? String(MedicineAndSolutionsParams.params.nrPrescriptionId),
      reasonNumber: reason,
      scheduleItemCicleStatus: YesNoType.NO,
      scheduleSequenceNumber: ScheduleMedicationParams.prototype.scheduleSequenceNumber,
      scheduleTime: schedulePrevious,
      statusAcm: statusAcm,
      statusApplication: MedicineAndSolutionsParams.params.ieViaApplicationId,
      unitMeasurementCode: MedicineAndSolutionsParams.params.uom,
      userName: foundationContext.getUser()?.username,
    }
  }

  scheduleTimeItem(): void {
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    const body = this.createScheduleTimeItemRequestBody();

    this.saveStandardSchedules();
    HttpService.post(
      Endpoint.SCHEDULE_TIME_ITEM_PRESCRIPTION, {}, { body }
    ).then(response => {
      const { message, inconsistentBatchDescription, eliminatedList } = response.body as ResponseConsist;

      applicationContext.redirect(ApplicationRoute.ADEP_ELETRONIC_SERVICE_PERSONAL_INFORMATION, {
        encounterId,
        patientId,
      });
      applicationContext.clearNavigationStack();
      if (message === eliminetedTime) {
        let toastMessage = '';
        if (eliminatedList != null && inconsistentBatchDescription != null) {
          toastMessage = this.replaceMessage(`${inconsistentBatchDescription} ${eliminatedList}`);
        } else if (eliminatedList != null) {
          toastMessage = this.replaceMessage(eliminatedList)
        } else if (inconsistentBatchDescription != null) {
          toastMessage = this.replaceMessage(inconsistentBatchDescription)
        }

        this.showToast(toastMessage, ToastLevel.WARNING)
        return;
      }

      if (message === exceededScheduleTime) {
        this.showToast(MessageService.translate('text.not-possible-reschedule-item'), ToastLevel.WARNING)
        return;
      }

      this.showToast('tws.schedule-medication-infusions-success', ToastLevel.SUCCESS);
    }).catch((err) => {
      const message = err.body.errors[0].replace(/#*/, '');
      this.showToast(message, ToastLevel.ERROR);
    }).finally(() => {
      this.formApi.setLoading(false)
    });
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'schedule-time-item-prescription',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  private replaceMessage(message: string): string {
    return MessageService.translate('tws.schedule-medication-infusions-time').replace('#@eliminados#@', `${message}`);
  }
}

export default ScheduleMedicationInfusionsController;
